import React, { useEffect, useState } from 'react';
import { updateAssetTags, requestAssetMeta, setAssetCapturedAt, setAssetPhotographer, setAssetSurfer } from '../data/firestore';
import {
  sampleAt,
  assetTimeMs,
  hasKnownTime,
  compass16,
  mToFt,
  kmhToKn,
  fmtFt,
  fmtKn,
  fmtPeriod,
  tideStateLabel,
  localTime,
} from '../utils/conditions';

const fmtRate = (v) => (v ? `${Math.round(v * 1000) / 1000} fps`.replace('.000', '') : '—');

// Some RED metadata items stringify to the camera's internal numeric enum code
// (e.g. sensor "15", camera "67108899") rather than a human label. Only surface
// values that read as a real name — i.e. contain a non-digit character.
const named = (v) => (v != null && /[a-zA-Z]/.test(String(v)) ? String(v) : undefined);

const pad2 = (n) => String(n).padStart(2, '0');
// epoch ms -> "YYYY-MM-DDTHH:MM" for a datetime-local input (viewer's tz).
const toLocalInput = (ms) => {
  if (!Number.isFinite(ms)) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const fmtDur = (s) => {
  if (!s) return '—';
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60);
  return m ? `${m}m ${sec}s` : `${sec}s`;
};
const fmtRecorded = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? String(s) : d.toLocaleString();
};

function Row({ label, children }) {
  if (children == null || children === '' || children === '—') return null;
  return (
    <div className="cs-meta-row">
      <span className="cs-meta-key">{label}</span>
      <span className="cs-meta-val">{children}</span>
    </div>
  );
}

// Collapsible technical-metadata + lineage + tags panel for an asset. Pulls
// from the ffprobe-derived `metadata`/`lineage` written by the backend.
export default function AssetDetails({ asset, tripId, cond }) {
  const [open, setOpen] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState(asset.tags || []);
  const [timeInput, setTimeInput] = useState('');
  useEffect(() => setTags(asset.tags || []), [asset.tags]);

  // Photographer credit: owner-set override falling back to the upload-time name.
  const uploadName = asset.lineage?.contributor || asset.contributor || '';
  const effectivePhotog = asset.photographer || uploadName;
  const [photog, setPhotog] = useState(effectivePhotog);
  const [photogSaved, setPhotogSaved] = useState(false);
  useEffect(() => {
    setPhotog(asset.photographer || asset.lineage?.contributor || asset.contributor || '');
  }, [asset.photographer, asset.lineage, asset.contributor]);
  const savePhotog = async () => {
    const next = photog.trim();
    try {
      await setAssetPhotographer(tripId, asset.id, next);
      setPhotogSaved(true);
      setTimeout(() => setPhotogSaved(false), 1600);
    } catch {
      /* leave the input as-is so the edit isn't lost */
    }
  };
  const photogDirty = photog.trim() !== (effectivePhotog || '');

  // Surfer tag: who's riding in the shot. Owner-set only (nothing captured at
  // upload time to fall back to).
  const [surfer, setSurfer] = useState(asset.surfer || '');
  const [surferSaved, setSurferSaved] = useState(false);
  useEffect(() => setSurfer(asset.surfer || ''), [asset.surfer]);
  const saveSurfer = async () => {
    try {
      await setAssetSurfer(tripId, asset.id, surfer.trim());
      setSurferSaved(true);
      setTimeout(() => setSurferSaved(false), 1600);
    } catch {
      /* leave the input as-is so the edit isn't lost */
    }
  };
  const surferDirty = surfer.trim() !== (asset.surfer || '');

  const m = asset.metadata || {};
  const v = m.video || {};
  const a = m.audio || {};
  const cam = m.camera || {};
  const red = asset.redMeta || (asset.isRawVideo ? { isRed: true, pending: true } : null);
  // RED slow-mo: footage shot at record_framerate but laid on a slower project
  // timeline plays in slow motion. The "true speed" multiplier (record/play) is
  // the speed-up needed to watch it at real-life speed.
  const redTrueX = red?.trueSpeedX
    || (red?.recordFps && red?.fps && red.recordFps > red.fps * 1.05
      ? Math.round((red.recordFps / red.fps) * 100) / 100
      : 0);
  const redTruePct = red?.trueSpeedPct || (redTrueX ? Math.round((redTrueX - 1) * 100) : 0);
  const status = asset.meta?.status;
  const fpsC = v.fpsContainer || asset.fpsContainer || 0;
  const fpsA = v.fpsAvg || asset.fpsAvg || 0;
  const capFps = asset.captureFps || 0;
  // Heuristic slow-motion flag: a meaningfully higher capture rate than the
  // container's average playback rate means the footage plays slowed down.
  const slowmo = capFps && fpsA && capFps > fpsA * 1.5;
  const lin = asset.lineage || {};
  const up = lin.uploader || null;
  const geo = (up && up.geo) || {};
  const gps = up && up.gps;
  const cl = (up && up.client) || {};

  // Surf conditions at this asset's exact capture time (if the trip series and a
  // capture time are both known). A manual override lets undated media line up.
  const condReady = !!(cond && cond.status === 'ready' && cond.hours?.length);
  const timeMs = assetTimeMs(asset);
  const sample = condReady && timeMs != null ? sampleAt(cond, timeMs) : null;
  const swellFt = sample ? mToFt(sample.swellH) : null;
  const windKn = sample ? kmhToKn(sample.windSpd) : null;
  const tideFt = sample ? mToFt(sample.tideM) : null;
  const isOverride = Number.isFinite(asset.capturedAtOverride);

  const saveTime = async () => {
    if (!timeInput) return;
    const ms = new Date(timeInput).getTime();
    if (Number.isFinite(ms)) await setAssetCapturedAt(tripId, asset.id, ms);
  };

  const commitTags = async (next) => {
    setTags(next);
    try {
      await updateAssetTags(tripId, asset.id, next);
    } catch {
      /* revert on failure */
      setTags(asset.tags || []);
    }
  };
  const addTag = () => {
    const t = tagInput.trim();
    if (!t) return;
    if (!tags.includes(t)) commitTags([...tags, t]);
    setTagInput('');
  };
  const removeTag = (t) => commitTags(tags.filter((x) => x !== t));

  return (
    <div className="cs-meta">
      <button
        type="button"
        className="cs-btn cs-btn-ghost cs-btn-sm"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title="Original metadata, lineage & tags"
      >
        ⓘ Details {status === 'probing' || status === 'queued' ? '· reading…' : ''}
      </button>

      {open && (
        <div className="cs-meta-panel" onClick={(e) => e.stopPropagation()}>
          {red && (
            <>
              <div className="cs-meta-sub">
                <span className="cs-red-tag">● RED R3D</span> camera negative
              </div>
              {red.pending && !asset.redMeta ? (
                <div className="cs-help" style={{ marginTop: 4 }}>
                  Confirmed RED R3D source. Detailed camera metadata (ISO, white
                  balance, color science) is captured on the next proxy rebuild.
                </div>
              ) : (
                <div className="cs-meta-grid">
                  <Row label="Camera">{[named(red.camera), red.firmware && `fw ${red.firmware}`].filter(Boolean).join(' · ')}</Row>
                  <Row label="Sensor">{[named(red.sensor), named(red.olpf)].filter(Boolean).join(' · ')}</Row>
                  <Row label="Color science">{red.colorScience}</Row>
                  <Row label="ISO">{red.iso}</Row>
                  <Row label="White balance">{[red.kelvin && `${red.kelvin}K`, Number.isFinite(red.tint) && red.tint !== 0 && `tint ${red.tint}`].filter(Boolean).join(' · ')}</Row>
                  <Row label="Shutter">{red.shutter}</Row>
                  <Row label="Aperture">{[red.aperture, red.focalLength].filter(Boolean).join(' · ')}</Row>
                  <Row label="Lens">{[named(red.lens), named(red.lensBrand)].filter(Boolean).join(' · ')}</Row>
                  <Row label="REDCODE">{named(red.redcode)}</Row>
                  <Row label="Resolution">{named(red.resolutionName)}</Row>
                  <Row label="Record rate">{[red.recordFps && `${red.recordFps} fps`, red.fps && red.recordFps && Math.abs(red.fps - red.recordFps) > 0.05 && `(project ${red.fps})`].filter(Boolean).join(' ')}</Row>
                  {redTrueX > 1 && (
                    <Row label="Real-time speed">{`${redTrueX}× faster (+${redTruePct}%) — plays at ${Math.round((1 / redTrueX) * 100)}% of life speed`}</Row>
                  )}
                  <Row label="In-camera trim">{[Number.isFinite(red.exposureAdjust) && red.exposureAdjust !== 0 && `${red.exposureAdjust > 0 ? '+' : ''}${red.exposureAdjust} EV`, Number.isFinite(red.saturation) && red.saturation !== 1 && `sat ${red.saturation}`, Number.isFinite(red.contrast) && red.contrast !== 1 && `con ${red.contrast}`].filter(Boolean).join(' · ')}</Row>
                </div>
              )}
            </>
          )}
          {(capFps || fpsC || fpsA) ? (
            <div className={`cs-fps-card ${slowmo ? 'is-slowmo' : ''}`}>
              <div className="cs-fps-line">
                <span>Recorded</span>
                <strong>{fmtRate(capFps || fpsC)}</strong>
              </div>
              <div className="cs-fps-line">
                <span>Plays at</span>
                <strong>{fmtRate(fpsA || fpsC)}</strong>
              </div>
              {slowmo ? (
                <div className="cs-fps-note">
                  Shot at {fmtRate(capFps)} but plays at {fmtRate(fpsA)} — this is roughly{' '}
                  <b>{Math.round((capFps / fpsA) * 10) / 10}× slow motion</b>. Set the clip speed to{' '}
                  {Math.round((capFps / fpsA) * 10) / 10}× in the editor to restore natural speed.
                </div>
              ) : (
                <div className="cs-fps-note">Capture and playback rate match — natural speed.</div>
              )}
            </div>
          ) : null}

          <div className="cs-meta-grid">
            <Row label="Resolution">{v.width ? `${v.width}×${v.height}` : asset.width ? `${asset.width}×${asset.height}` : null}</Row>
            <Row label="Codec">{[v.codec, v.profile].filter(Boolean).join(' · ')}</Row>
            <Row label="Container">{m.container}</Row>
            <Row label="Duration">{fmtDur(m.durationSec)}</Row>
            <Row label="Container fps">{fpsC ? fmtRate(fpsC) : null}</Row>
            <Row label="Average fps">{fpsA ? fmtRate(fpsA) : null}</Row>
            <Row label="Capture fps">{capFps ? fmtRate(capFps) : null}</Row>
            <Row label="Color">{[v.colorSpace, v.colorPrimaries, v.colorTransfer].filter(Boolean).join(' / ')}</Row>
            <Row label="Pixel fmt">{v.pixFmt}</Row>
            <Row label="Bitrate">{m.bitRate ? `${Math.round(m.bitRate / 1000)} kbps` : null}</Row>
            <Row label="Audio">{a.codec ? `${a.codec} · ${a.channels || 0}ch · ${a.sampleRate || 0} Hz` : null}</Row>
            <Row label="Camera">{[cam.make, cam.model].filter(Boolean).join(' ') || asset.camera}</Row>
            <Row label="Software">{cam.software}</Row>
            <Row label="Recorded">{fmtRecorded(m.recordedAt || asset.recordedAt)}</Row>
            <Row label="Location">{m.location}</Row>
          </div>

          {condReady && (
            <>
              <div className="cs-meta-sub">Conditions {sample ? `· ${localTime(cond, sample.t)}` : ''}</div>
              {sample ? (
                <div className="cs-meta-grid">
                  <Row label="Swell">
                    {swellFt != null ? `${fmtFt(swellFt)} @ ${fmtPeriod(sample.swellP)} ${compass16(sample.swellDir)}`.trim() : null}
                  </Row>
                  <Row label="Wind">
                    {windKn != null ? `${fmtKn(windKn)} ${compass16(sample.windDir)}`.trim() : null}
                  </Row>
                  <Row label="Tide">
                    {tideFt != null ? `${fmtFt(tideFt)}${sample.tideState ? ` · ${tideStateLabel(sample.tideState)}` : ''}` : null}
                  </Row>
                  <Row label="Time source">{isOverride ? 'manual' : hasKnownTime(asset) ? 'recorded' : null}</Row>
                </div>
              ) : (
                <div className="cs-cond-asset-set">
                  <span className="cs-help" style={{ margin: 0 }}>
                    {timeMs == null
                      ? 'No capture time on this file — set when it was shot to pin its conditions.'
                      : 'This time is outside the trip’s conditions window.'}
                  </span>
                </div>
              )}
              <div className="cs-cond-asset-set">
                <input
                  className="cs-input cs-input-sm"
                  type="datetime-local"
                  value={timeInput || toLocalInput(timeMs)}
                  onChange={(e) => setTimeInput(e.target.value)}
                />
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={saveTime}>
                  {timeMs == null ? 'Set time' : 'Update'}
                </button>
                {isOverride && (
                  <button
                    type="button"
                    className="cs-btn cs-btn-ghost cs-btn-sm"
                    onClick={() => { setTimeInput(''); setAssetCapturedAt(tripId, asset.id, null); }}
                    title="Remove the manual time and use the recorded date"
                  >
                    Clear
                  </button>
                )}
              </div>
            </>
          )}

          <div className="cs-meta-sub">Photographer</div>
          <div className="cs-tag-add">
            <input
              className="cs-input cs-input-sm"
              placeholder="Who shot this? (e.g. Justin)"
              value={photog}
              onChange={(e) => setPhotog(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); savePhotog(); }
              }}
            />
            <button
              type="button"
              className="cs-btn cs-btn-primary cs-btn-sm"
              onClick={savePhotog}
              disabled={!photogDirty}
            >
              {photogSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>
          {asset.photographer && uploadName && asset.photographer !== uploadName && (
            <div className="cs-help" style={{ marginTop: 4 }}>
              Uploaded as “{uploadName}”.{' '}
              <button
                type="button"
                onClick={() => { setPhotog(''); setAssetPhotographer(tripId, asset.id, ''); }}
                style={{
                  background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                  color: 'var(--surf)', textDecoration: 'underline', font: 'inherit',
                }}
              >
                Reset to uploaded name
              </button>
            </div>
          )}

          <div className="cs-meta-sub">Surfer</div>
          <div className="cs-tag-add">
            <input
              className="cs-input cs-input-sm"
              placeholder="Who's riding? (e.g. Jito)"
              value={surfer}
              onChange={(e) => setSurfer(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); saveSurfer(); }
              }}
            />
            <button
              type="button"
              className="cs-btn cs-btn-primary cs-btn-sm"
              onClick={saveSurfer}
              disabled={!surferDirty}
            >
              {surferSaved ? '✓ Saved' : 'Save'}
            </button>
          </div>

          <div className="cs-meta-sub">Lineage</div>
          <div className="cs-meta-grid">
            <Row label="Origin">{lin.origin === 'drop' ? 'guest drop link' : lin.origin || 'upload'}</Row>
            <Row label="Via">{lin.via}</Row>
            <Row label="Original name">{lin.originalName !== asset.name ? lin.originalName : null}</Row>
          </div>

          {up && (
            <>
              <div className="cs-meta-sub">Uploader (records)</div>
              <div className="cs-meta-grid">
                <Row label="IP address">{up.ip}</Row>
                <Row label="Proxy hops">{Array.isArray(up.ipChain) && up.ipChain.length > 1 ? up.ipChain.join(' → ') : null}</Row>
                <Row label="City">{geo.city}</Row>
                <Row label="Region">{geo.region}</Row>
                <Row label="Country">{[geo.country, geo.countryCode && `(${geo.countryCode})`].filter(Boolean).join(' ')}</Row>
                <Row label="ISP / org">{[geo.isp, geo.org].filter(Boolean).join(' · ')}</Row>
                <Row label="Network">{geo.asn}</Row>
                <Row label="GPS">{gps ? `${gps.lat?.toFixed?.(5)}, ${gps.lng?.toFixed?.(5)}${gps.accuracyM ? ` (±${Math.round(gps.accuracyM)}m)` : ''}` : null}</Row>
                <Row label="Device id">{cl.fingerprintId}</Row>
                <Row label="Platform">{[cl.platform, cl.vendor].filter(Boolean).join(' · ')}</Row>
                <Row label="Browser">{(cl.userAgent || up.userAgent || '').slice(0, 90)}</Row>
                <Row label="Timezone">{cl.timezone}</Row>
                <Row label="Screen">{cl.screen ? `${cl.screen.w}×${cl.screen.h} @${cl.screen.dpr}x` : null}</Row>
                <Row label="Languages">{Array.isArray(cl.languages) && cl.languages.length ? cl.languages.join(', ') : null}</Row>
                <Row label="Captured">{fmtRecorded(up.observedAt || up.capturedAt)}</Row>
              </div>
              {gps && (
                <a
                  className="cs-btn cs-btn-ghost cs-btn-sm"
                  style={{ marginTop: 6 }}
                  href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  View GPS on map
                </a>
              )}
              <div className="cs-help" style={{ marginTop: 6 }}>
                MAC / hardware addresses can’t be read by a web browser, so they’re never captured.
              </div>
            </>
          )}

          <div className="cs-meta-sub">Tags</div>
          <div className="cs-tag-row">
            {tags.length === 0 && <span className="cs-help" style={{ margin: 0 }}>No tags yet.</span>}
            {tags.map((t) => (
              <span key={t} className="cs-tag">
                {t}
                <button type="button" className="cs-tag-x" onClick={() => removeTag(t)} title="Remove tag">×</button>
              </span>
            ))}
          </div>
          <div className="cs-tag-add">
            <input
              className="cs-input cs-input-sm"
              placeholder="Add a tag (e.g. drone, slow-mo, Alex)…"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); addTag(); }
              }}
            />
            <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={addTag}>Add</button>
          </div>

          {status === 'error' && (
            <div className="cs-meta-err">
              Metadata read failed.
              <button
                type="button"
                className="cs-btn cs-btn-ghost cs-btn-sm"
                onClick={() => requestAssetMeta(tripId, asset.id)}
              >
                Retry
              </button>
            </div>
          )}
          {(status === 'none' || (!asset.metadata && status === 'ready')) && (
            <div className="cs-help" style={{ marginTop: 6 }}>
              No probe metadata for this format.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
